import {
  CurrencyValue,
  Chart,
  formatCurrency,
  ChartSkeleton,
  formatDatetime,
  assetNameToRgba
} from '@blockanalitica/ui';
import { useMemo, useCallback, ReactNode } from 'react';
import {
  ChartConfig,
  ChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/Chart';

export interface DatesData {
  [underlying_symbol: string]: {
    period: string;
    collateral_amount_usd: string;
  }[];
}

export default function DatesLiquidationGraph({
  data: auctionData,
  timeFilter,
  chartDaysAgo
}: {
  data: DatesData;
  timeFilter?: ReactNode;
  chartDaysAgo: string | number;
}) {
  const periodInfo = useMemo(() => {
    const value = Number(chartDaysAgo);
    if (value < 90) {
      return {
        prefix: '',
        dateFormat: 'MMM dd, yyyy'
      };
    } else if (value < 365) {
      return {
        prefix: 'Week starting ',
        dateFormat: 'MMM dd, yyyy'
      };
    }
    return {
      prefix: '',
      dateFormat: 'MMM yyyy'
    };
  }, [chartDaysAgo]);

  const chartDatasets: ChartDataset[] = useMemo(() => {
    return (
      auctionData &&
      Object.entries(auctionData).map(([key, rows]) => ({
        label: key,
        data: rows.map((row) => ({
          x: row['period'],
          y: row['collateral_amount_usd']
        })),
        borderColor: assetNameToRgba(key) || '#fff',
        backgroundColor: assetNameToRgba(key) || '#fff'
      }))
    );
  }, [auctionData]);

  const valueFormatter = useCallback(
    (value: string | number) => (
      <CurrencyValue
        value={value}
        variant="dynamic"
        options={{
          currency: 'USD'
        }}
      />
    ),
    []
  );

  const subValueFormatter = useCallback(
    (value: string | number) => {
      return `${periodInfo.prefix}${formatDatetime(value)}`;
    },
    [periodInfo]
  );

  const config: ChartConfig = useMemo(() => {
    return {
      type: 'bar',
      options: {
        scales: {
          x: {
            stacked: true
          },
          y: {
            stacked: true
          }
        },
        elements: {
          bar: {
            hoverBorderWidth: 0
          }
        }
      }
    };
  }, []);

  if (auctionData) {
    return (
      <Chart
        chartDatasets={chartDatasets}
        config={config}
        timeFilter={timeFilter}
        valueFormatter={valueFormatter}
        subValueFormatter={subValueFormatter}
        tooltip={{
          valueFormatter: (value) => formatCurrency(Number(value)),
          titleFormatter: (_value, tootlipItems) =>
            `${periodInfo.prefix}${formatDatetime((tootlipItems[0].raw as { x: string }).x, { format: periodInfo.dateFormat })}`
        }}
        showKpisAsTooltip
        daysAgo={chartDaysAgo}
      />
    );
  }
  return <ChartSkeleton />;
}
